import { FC } from 'react';
import cn from 'classnames';
import parse from 'html-react-parser';

import { Button } from '@catawiki/cata-style';

import { BuilderString, CtaVariant } from '@/shared/types';
import {
  getCleanInput,
  isBuilderStringPresent,
  SanitizeMode,
  someBuilderStringPresent,
} from '@/shared/text';

import { flatMediaInput, FlatMediaInput, Media, MediaType } from '../Media';
import { ActionVariant, getActionVariantName } from '../Action';
import styles from './Topic.module.scss';
import { List, listInput, ListItem } from './List';

export const topicInput = (name: string, friendlyName: string) => ({
  name,
  friendlyName,
  type: 'object',
  defaultValue: {
    title: 'Content Title',
    subtitle: 'Content Subtitle',
    description: 'Content Description',
    mediaType: 'none',
    actionVariant: 'none',
  },
  subFields: [
    { name: 'title', type: 'CatawikiEditor' },
    listInput('listItems', 'List Items'),
    ...flatMediaInput({ useCatawikiPlainEditor: true }),
    {
      name: 'actionLabel',
      type: 'CatawikiPlainEditor',
      friendlyName: 'Button Text',
    },
    {
      name: 'actionUrl',
      type: 'CatawikiPlainEditor',
      friendlyName: 'Button URL',
    },
    {
      name: 'actionVariant',
      friendlyName: 'Button Type',
      type: 'string',
      enum: Object.values(ActionVariant).map((actionVariantName) => ({
        label: getActionVariantName(actionVariantName),
        value: actionVariantName,
      })),
      defaultValue: ActionVariant.NONE.toString(),
    },
  ],
});

export interface Topic extends FlatMediaInput {
  title: BuilderString;
  listItems: ListItem[];

  actionLabel?: BuilderString;
  actionUrl?: BuilderString;
  actionVariant: CtaVariant;
}

interface Props extends Topic {
  className?: string;
  locale: string;
}

const TEXT_SPACING = 'u-m-t-sm-m u-m-t-md-m u-m-t-lg-xl';

export const Topic: FC<Props> = ({
  title,
  listItems,
  actionUrl,
  actionVariant,
  actionLabel,
  locale,
  className,

  mediaType,
  mediaCaption,
  imageUrl,
  imageLink,
  imageLinkTarget,
  videoUrl,
  videoAutoPlay,
  youtubeUrl,
  youtubeDisabledDialog,
}) => (
  <div className={className}>
    <Media
      locale={locale}
      containerClassName={styles.media}
      mediaType={mediaType}
      mediaCaption={mediaCaption}
      image={imageUrl}
      imageLink={imageLink}
      imageLinkTarget={imageLinkTarget}
      video={{
        source: videoUrl || '',
        autoPlay: videoAutoPlay,
        poster: imageUrl,
      }}
      youtube={
        youtubeUrl
          ? {
              url: youtubeUrl || '',
              poster: imageUrl || '',
              disabledDialog: youtubeDisabledDialog,
            }
          : undefined
      }
    />
    <div className={cn({ 'u-m-t-xl': mediaType !== MediaType.NONE })}>
      {isBuilderStringPresent(title, locale) ? (
        <div className={cn('u-typography-h3')}>
          {parse(getCleanInput(locale, SanitizeMode.SINGLE_LINE, title))}
        </div>
      ) : null}
      {listItems?.length ? (
        <List
          className={cn({
            [TEXT_SPACING]: someBuilderStringPresent([title], locale),
          })}
          locale={locale}
          items={listItems}
        />
      ) : null}
      {actionVariant === 'none' ? null : (
        <Button
          className={cn({
            [TEXT_SPACING]:
              someBuilderStringPresent([title], locale) || listItems?.length,
          })}
          variant={actionVariant}
          as="a"
          href={getCleanInput(
            locale,
            SanitizeMode.SINGLE_LINE_TEXT_ONLY,
            actionUrl
          )}
        >
          {getCleanInput(
            locale,
            SanitizeMode.SINGLE_LINE_TEXT_ONLY,
            actionLabel
          )}
        </Button>
      )}
    </div>
  </div>
);
